/* eslint-disable max-len */
import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

const defaultContextValue = Object.freeze({
  count: 0,
});

const defaultScopedContextValue = Object.freeze({
  scopedCount: 0,
});

const defaultTotalContextValue = Object.freeze({
  totalCount: defaultContextValue.count + defaultScopedContextValue.scopedCount,
});

const defaultActionContextValue = Object.freeze({
  increment: _.noop,
  incrementScoped: _.noop,
  decrement: _.noop,
  decrementScoped: _.noop,
});

/**
 * Generate a Context Provider Component for the specified
 *    CounterContext, CounterScopedContext, CounterTotalContext and CounterActionContext.
 * The CounterContext provides:
 *    count: the increment count coming from the current Context.
 * The CounterScopedContext provides:
 *    scopedCount: the scoped increment count coming from all the scopes of child Contextes.
 * The CounterTotalContext provides:
 *    totalCount: the sum of count and scopedCount.
 * The CounterActionContext provides:
 *    increment: the increment function that will add 1 to count,
 *               which will also propagate the addition to parent context's scopedCount
 *               by calling parent context's incrementScoped function.
 *    incrementScoped: the scoped increment function that will add 1 to scopedCount,
 *               which will also propagate the addition to parent context's scopedCount
 *               by calling parent context's incrementScoped function.
 *    decrement: the decrement function that will subtract 1 from count,
 *               which will also propagate the subtraction to parent context's scopedCount
 *               by calling parent context's decrementScoped function.
 *    decrementScoped: the scoped decrement function that will subtract 1 from scopedCount,
 *               which will also propagate the subtraction to parent context's scopedCount
 *               by calling parent context's decrementScoped function.
 * @param {object} CounterContext - The CounterContext object that the Provider is generated for.
 * @param {object} CounterScopedContext - The CounterScopedContext object that the Provider is generated for.
 * @param {object} CounterTotalContext - The CounterTotalContext object that the Provider is generated for.
 * @param {object} CounterActionContext - The CounterActionContext object that the Provider is generated for.
 * @param {object} options - The options that can be configured.
 * @param {string} options.name - The Counter name used for formatting Provider displayName.
 * @returns {React.Component} - The generated CounterContext Provider.
 */
export const generateCounterContextProvider = (
  CounterContext,
  CounterScopedContext,
  CounterTotalContext,
  CounterActionContext,
  { name }
) => {
  const Provider = ({ children }) => {
    const {
      incrementScoped: incrementScopedUpper,
      decrementScoped: decrementScopedUppper,
    } = useContext(CounterActionContext);

    // Counter counts
    const [count, setCount] = useState(0);
    const [scopedCount, setScopedCount] = useState(0);
    const totalCount = count + scopedCount;

    // Counter actions
    const increment = useCallback(() => {
      setCount(prevCount => prevCount + 1);
      incrementScopedUpper();
    }, [incrementScopedUpper]);
    const decrement = useCallback(() => {
      setCount(prevCount => prevCount - 1);
      decrementScopedUppper();
    }, [decrementScopedUppper]);
    const incrementScoped = useCallback(() => {
      setScopedCount(prevCount => prevCount + 1);
      incrementScopedUpper();
    }, [incrementScopedUpper]);
    const decrementScoped = useCallback(() => {
      setScopedCount(prevCount => prevCount - 1);
      decrementScopedUppper();
    }, [decrementScopedUppper]);

    // Memoize different context values
    const memoCount = useMemo(() => ({ count }), [count]);
    const memoScopedCount = useMemo(() => ({ scopedCount }), [scopedCount]);
    const memoTotalCount = useMemo(() => ({ totalCount }), [totalCount]);
    const memoActions = useMemo(() => ({
      increment,
      incrementScoped,
      decrement,
      decrementScoped,
    }), [decrement, decrementScoped, increment, incrementScoped]);

    // Split the Contextes for perf optimization, for usage, only use the Context that is needed
    return (
      <CounterContext.Provider value={memoCount}>
        <CounterScopedContext.Provider value={memoScopedCount}>
          <CounterTotalContext.Provider value={memoTotalCount}>
            <CounterActionContext.Provider value={memoActions}>
              {children}
            </CounterActionContext.Provider>
          </CounterTotalContext.Provider>
        </CounterScopedContext.Provider>
      </CounterContext.Provider>
    );
  };

  Provider.displayName = `${name}CounterContextProvider`;
  Provider.propTypes = {
    children: PropTypes.node.isRequired,
  };

  return Provider;
};

/**
 * A simple hook for using the value of the specified CounterContext.
 * @param {object} CounterContext - The CounterContext object that the hook is generated for.
 * @returns {function} - The generated CounterContext hook.
 */
export const generateCounterContextHook = CounterContext => () => useContext(CounterContext);

/**
 * Generate the HOC to wrap Component with a specified CounterContext Provider.
 * @param {React.Component} CounterContextProvider - The specified CounterContext Provider.
 * @returns {function} - The HOC.
 */
export const generateCounterContextProviderHOC = CounterContextProvider => (WrappedComponent) => {
  const HOC = props => (
    <CounterContextProvider>
      <WrappedComponent {...props} />
    </CounterContextProvider>
  );

  HOC.displayName = `With${getDisplayName(CounterContextProvider)}(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
};

/**
 * Generate a set of utils for a Counter.
 * @param {object} options - The options that can be configured.
 * @param {string} options.name - The Counter name.
 * @returns {object} - The Counter utils:
 *    useCounterContext: the simple hook for using the value of the CounterContext.
 *    useCounterScopedContext: the simple hook for using the value of the CounterScopedContext.
 *    useCounterTotalContext: the simple hook for using the value of the CounterTotalContext.
 *    useCounterActionContext: the simple hook for using the value of the CounterActionContext.
 *    CounterContextProvider: the Provider Component for
 *        CounterContext, CounterScopedContext, CounterTotalContext and CounterActionContext.
 *    withCounterContextProvider: the HOC to wrap Component with CounterContextProvider.
 */
export const generateCounter = ({ name }) => {
  const CounterContext = createContext(defaultContextValue);
  const CounterScopedContext = createContext(defaultScopedContextValue);
  const CounterTotalContext = createContext(defaultTotalContextValue);
  const CounterActionContext = createContext(defaultActionContextValue);
  const useCounterContext = generateCounterContextHook(CounterContext);
  const useCounterScopedContext = generateCounterContextHook(CounterScopedContext);
  const useCounterTotalContext = generateCounterContextHook(CounterTotalContext);
  const useCounterActionContext = generateCounterContextHook(CounterActionContext);
  const CounterContextProvider = generateCounterContextProvider(
    CounterContext,
    CounterScopedContext,
    CounterTotalContext,
    CounterActionContext,
    { name }
  );
  const withCounterContextProvider = generateCounterContextProviderHOC(CounterContextProvider);

  return {
    useCounterContext,
    useCounterScopedContext,
    useCounterTotalContext,
    useCounterActionContext,
    CounterContextProvider,
    withCounterContextProvider,
  };
};
